
class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    // AAS-5792: a sort pick is a single deliberate choice — submit it at once.
    // Checkboxes and price inputs keep the debounce so rapid ticks batch up.
    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', (event) => {
      if (event.target.name === 'sort_by') {
        this.onSubmitHandler(event);
      } else {
        this.debouncedOnSubmit(event);
      }
    });

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
    viewMode();
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const countContainerMobile = document.getElementById('ProductCountMobile');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    const gridContainer = document.getElementById('ProductGridContainer');
    gridContainer.querySelector('.collection').classList.add('loading');
    gridContainer.setAttribute('aria-busy', 'true');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }
    if (countContainerMobile) {
      countContainerMobile.classList.add('loading');
    }

    // AAS-5792: only the latest call may paint. A quick second sort/filter pick
    // must not be overwritten by the slower response of the first one, and a
    // repeat of the same state (debounced filter + instant sort) paints once.
    FacetFiltersForm.renderId += 1;
    const { renderId } = FacetFiltersForm;
    const isLatest = () => renderId === FacetFiltersForm.renderId;

    sections.forEach(({ section }) => {
      const url = FacetFiltersForm.getSectionUrl(section, searchParams);
      const cached = FacetFiltersForm.filterData.find((element) => element.url === url);

      if (cached) {
        FacetFiltersForm.renderSection(cached.html, event);
        return;
      }

      // A failed request (e.g. a shared prefetch that was rate limited) gets one
      // fresh retry; after that, never leave the grid dimmed — load the page itself.
      FacetFiltersForm.fetchSection(url)
        .catch(() => new Promise((resolve) => setTimeout(resolve, 300)).then(() => FacetFiltersForm.fetchSection(url)))
        .then((html) => {
          if (isLatest()) FacetFiltersForm.renderSection(html, event);
        })
        .catch(() => {
          if (isLatest()) window.location.assign(`${window.location.pathname}?${searchParams}`);
        });
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
    FacetFiltersForm.syncSortSelects(searchParams);
  }

  static getSectionUrl(sectionId, searchParams) {
    return `${window.location.pathname}?section_id=${sectionId}&${searchParams}`;
  }

  // AAS-5792: a section render takes Shopify 1–2 s, so requests are shared —
  // a click on a sort option that is still prefetching reuses that request
  // instead of starting a second one. The cache is capped because every
  // response is a full section (~1 MB of HTML).
  static fetchSection(url) {
    const { pendingFetches } = FacetFiltersForm;
    if (!pendingFetches.has(url)) {
      const request = fetch(url)
        .then((response) => {
          if (!response.ok) throw Object.assign(new Error(`Section request failed: ${response.status}`), { status: response.status });
          return response.text();
        })
        .then((html) => {
          FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }].slice(
            -FacetFiltersForm.cacheLimit
          );
          return html;
        })
        .finally(() => pendingFetches.delete(url));
      pendingFetches.set(url, request);
    }
    return pendingFetches.get(url);
  }

  // AAS-5792: called on sort-dropdown intent (armada-filter-panel.js). Warms the
  // cache with every other sort for the current filters, so the pick that follows
  // renders from memory instead of waiting on Shopify. Queued (most-used sorts
  // first, a few at a time) and paused on a 429 so we never trip rate limiting.
  static prefetchSortOptions() {
    if (navigator.connection?.saveData || Date.now() < FacetFiltersForm.prefetchPausedUntil) return;
    const sortSelect = document.getElementById('ArmadaSortBy') || document.getElementById('SortBy');
    if (!sortSelect) return;

    const { prefetchPriority } = FacetFiltersForm;
    const rank = (value) => (prefetchPriority.includes(value) ? prefetchPriority.indexOf(value) : prefetchPriority.length);
    const [{ section }] = FacetFiltersForm.getSections();

    // Replace the queue: only the current filter state is worth fetching.
    FacetFiltersForm.prefetchQueue = Array.from(sortSelect.options, ({ value }) => value)
      .filter((value) => value !== sortSelect.value)
      .sort((a, b) => rank(a) - rank(b))
      .map((value) =>
        FacetFiltersForm.getSectionUrl(section, FacetFiltersForm.buildSearchParams(sortSelect.form, value).toString())
      );
    FacetFiltersForm.drainPrefetchQueue();
  }

  static drainPrefetchQueue() {
    const { pendingFetches } = FacetFiltersForm;
    while (
      FacetFiltersForm.prefetchActive < FacetFiltersForm.prefetchConcurrency &&
      FacetFiltersForm.prefetchQueue.length
    ) {
      const url = FacetFiltersForm.prefetchQueue.shift();
      if (pendingFetches.has(url) || FacetFiltersForm.filterData.some((element) => element.url === url)) continue;

      FacetFiltersForm.prefetchActive += 1;
      FacetFiltersForm.fetchSection(url)
        .catch((error) => {
          if (error.status !== 429) return;
          FacetFiltersForm.prefetchQueue = [];
          FacetFiltersForm.prefetchPausedUntil = Date.now() + 30000;
        })
        .finally(() => {
          FacetFiltersForm.prefetchActive -= 1;
          FacetFiltersForm.drainPrefetchQueue();
        });
    }
  }

  // AAS-5792: #ArmadaSortBy lives outside the re-rendered grid, so on
  // back/forward it would keep the last pick. Mirror the URL's sort into every
  // sort select; with no sort_by, fall back to the option rendered as selected.
  static syncSortSelects(searchParams) {
    const sortBy = new URLSearchParams(searchParams).get('sort_by');
    document.querySelectorAll('select[name="sort_by"]').forEach((select) => {
      const fallback = Array.from(select.options).find((option) => option.defaultSelected);
      const value = sortBy || (fallback && fallback.value);
      if (value) select.value = value;
    });
  }

  // Parse the (large) section response once and hand the document to each renderer.
  static renderSection(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    FacetFiltersForm.renderFilters(parsedHTML, event);
    FacetFiltersForm.renderProductGridContainer(parsedHTML);
    FacetFiltersForm.renderProductCount(parsedHTML);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(parsedHTML) {
    const container = document.getElementById('ProductGridContainer');
    const source = parsedHTML.getElementById('ProductGridContainer');
    // AAS-5792: the reveal-on-scroll cascade (75 ms per tile + --duration-extended)
    // made the re-sorted grid fade in over ~1 s. Cancel it for the swap (animateGridIn
    // plays a quicker one); tiles below the fold still reveal on scroll.
    source.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'));
    container.innerHTML = source.innerHTML;
    container.removeAttribute('aria-busy');
    FacetFiltersForm.animateGridIn(container);

    const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10);
    const posTop = container.offsetTop - headerHeight - 50;
    window.scrollTo({ top: posTop, behavior: 'smooth' });

    viewMode();
  }

  // AAS-5792: a short staggered fade-up for the tiles on screen after a sort or
  // filter swap (styles in armada-filter-panel.css). The class comes off once
  // the tile has animated, so tiles below the fold keep the theme's own reveal.
  static animateGridIn(container) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    // Only tiles actually in view: promo tiles hidden at this breakpoint have no
    // box and would never fire animationend, while still delaying the stagger.
    const tiles = Array.from(container.querySelectorAll('#product-grid > li:not([data-regios-payload])')).filter(
      (tile) => {
        const { top, bottom, height } = tile.getBoundingClientRect();
        return height > 0 && bottom > 0 && top < window.innerHeight;
      }
    );
    tiles.forEach((tile, index) => {
      tile.style.setProperty('--swap-order', index);
      tile.classList.add('armada-swap-in');
      const onEnd = (event) => {
        if (event.target !== tile) return; // ignore animations bubbling up from inside the card
        tile.classList.remove('armada-swap-in');
        tile.removeEventListener('animationend', onEnd);
      };
      tile.addEventListener('animationend', onEnd);
    });
  }

  static renderProductCount(parsedHtml) {
    const productCountElement = parsedHtml.getElementById('ProductCount');
    const productCountDesktopElement = parsedHtml.getElementById('ProductCountDesktop');
    const productCountMobileElement = parsedHtml.getElementById('ProductCountMobile');
    const productCountActiveElement = parsedHtml.getElementById('ProductCountActive');
    const countNewActive = productCountActiveElement ? productCountActiveElement.innerHTML : '0';
    

    const updateContainer = (containerId, count) => {
        const container = document.getElementById(containerId);
        if (container) {
            container.innerHTML = count;
            container.classList.remove('loading');
        }
    };

    if (productCountElement) {
        updateContainer('ProductCount', productCountElement.innerHTML);
    }
    if (productCountDesktopElement) {
        updateContainer('ProductCountDesktop', productCountDesktopElement.innerHTML);
    }
    if (productCountMobileElement) {
        updateContainer('ProductCountMobile', productCountMobileElement.innerHTML);
    }

    const countActive = document.getElementById('ProductCountActive');
    if (countActive) {
        countActive.innerHTML = countNewActive;
    }

    const loadingSpinners = document.querySelectorAll(
        '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach(spinner => spinner.classList.add('hidden'));
  }


  static renderFilters(parsedHTML, event) {
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          console.log(previousElementClassName, previousElementId);
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
    reinitializeCustomElements();
    viewMode();
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    //document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    // Re-picking the current state replaces the entry, so Back never needs two presses.
    const method = searchParams === window.location.search.slice(1) ? 'replaceState' : 'pushState';
    history[method]({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const searchParams = FacetFiltersForm.buildSearchParams(event.target.closest('form')).toString();
    // AAS-5792: a filter tick followed by a sort within the debounce window fires
    // twice with the same state — the grid is already showing/loading it, so
    // repainting would only flash the tiles again.
    if (searchParams === FacetFiltersForm.searchParamsPrev) return;
    this.onSubmitForm(searchParams, event);
  }

  // AAS-5792: one source of truth per param. Filters always come from the live
  // filter panel form (its inputs reflect every AJAX pick), the sort from
  // `sortValue`, the select in the form that fired, or the Armada sort select.
  // Previously a sort serialized the page-load filter snapshot in
  // #FacetSortDrawerForm, and a filter tick serialized only the panel form,
  // which has no sort_by — each dropped the other. The sort prefetch builds its
  // URLs here too, so they match the cache keys of a real pick exactly.
  static buildSearchParams(sourceForm, sortValue) {
    const sortDrawerForm = document.getElementById('FacetSortDrawerForm');
    const filtersForm =
      document.getElementById('FacetFiltersFormMobile') || document.getElementById('FacetFiltersForm');

    const searchParams = FacetFiltersForm.createFilteredSearchParams(filtersForm || sortDrawerForm || sourceForm);

    const sortSelect =
      sourceForm?.querySelector('select[name="sort_by"]') ||
      document.getElementById('ArmadaSortBy') ||
      document.getElementById('SortBy');
    const sortBy = sortValue || sortSelect?.value;
    if (sortBy) searchParams.set('sort_by', sortBy);

    // Search terms / vendor-type query live on whichever form rendered them.
    [sourceForm, sortDrawerForm].forEach((form) => {
      if (!form) return;
      const formData = new FormData(form);
      ['q', 'options[prefix]'].forEach((key) => {
        if (!searchParams.has(key) && formData.get(key)) searchParams.set(key, formData.get(key));
      });
    });

    return searchParams;
  }

  static createFilteredSearchParams(form) {
    const formData = new FormData(form);
    const searchParams = new URLSearchParams(formData);

    if (searchParams.has('filter.v.price.gte') && searchParams.get('filter.v.price.gte') === '0') {
      searchParams.delete('filter.v.price.gte');
    }

    const priceSlider = document.querySelector('.price-slider-range');
    if (priceSlider) {
      const maxPrice = parseFloat(priceSlider.dataset.max);

      if (searchParams.has('filter.v.price.lte') && searchParams.get('filter.v.price.lte') === maxPrice.toString()) {
        searchParams.delete('filter.v.price.lte');
      }
    }

    return searchParams;
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const { searchParams } = new URL(event.currentTarget.href, window.location.href);
    // AAS-5792: "Clear all" is a static link to the bare collection URL (or the
    // page-load sort on search), so removing filters also reset the sort. Keep
    // the shopper's current sort on every remove link.
    const currentSort = new URLSearchParams(window.location.search).get('sort_by');
    if (currentSort) searchParams.set('sort_by', currentSort);
    FacetFiltersForm.renderPage(searchParams.toString());
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.pendingFetches = new Map();
FacetFiltersForm.cacheLimit = 24;
FacetFiltersForm.renderId = 0;
FacetFiltersForm.prefetchQueue = [];
FacetFiltersForm.prefetchActive = 0;
FacetFiltersForm.prefetchConcurrency = 3;
FacetFiltersForm.prefetchPausedUntil = 0;
FacetFiltersForm.prefetchPriority = ['price-ascending', 'price-descending', 'best-selling', 'created-descending'];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
    this.priceSlider();
  }
  connectedCallback(){
    this.priceSlider();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }


  priceSlider() {
      const sliderRange = this.querySelector(".price-slider-range");
      if (!sliderRange) return;

      const [minInput, maxInput] = this.querySelectorAll('input');
      const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
      const event = new CustomEvent('input');

      const minValue = parseFloat(minInput.value.replace(',', '')) || 0;
      const maxValue = parseFloat(maxInput.value.replace(',', '')) || parseFloat(sliderRange.dataset.max);

      if (sliderRange.noUiSlider) {
          sliderRange.noUiSlider.destroy();
      }
      const direction = document.documentElement.dir === 'rtl' ? 'rtl' : 'ltr';

      noUiSlider.create(sliderRange, {
          start: [minValue, maxValue],
          direction: direction,
          connect: true,
          step: 10,
          handleAttributes: [
            { 'aria-label': 'lower' },
            { 'aria-label': 'upper' },
          ],
          range: {
              'min': 0,
              'max': parseFloat(sliderRange.dataset.max)
          }
      });
      
      sliderRange.noUiSlider.on('update', (values) => {
          minInput.value = Math.round(parseFloat(values[0]));
          maxInput.value = Math.round(parseFloat(values[1]));
      });
      let debounceTimer;

      sliderRange.noUiSlider.on('set', (values) => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          minInput.value = Math.round(parseFloat(values[0]));
          maxInput.value = Math.round(parseFloat(values[1]));
          form.querySelector('form')?.dispatchEvent(event);
        }, 100);
      });

  }
  
}

customElements.define('price-range', PriceRange);


class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    // Stop the click reaching document-level handlers (page-transition.js would
    // otherwise navigate to the link's href and reload the page).
    event.stopPropagation();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);


function reinitializeCustomElements() {
  const container = document.querySelector('.section-collection-products-grid');
  if (!container) return;

  container.querySelectorAll('collapsible-details').forEach((element) => {
    const isOpen = element.querySelector('details')?.open;

    const newElement = element.cloneNode(true);
    element.replaceWith(newElement);

    // NOTE: do NOT call connectedCallback() manually here — the browser invokes
    // it automatically when the clone is inserted. Calling it again created
    // duplicate (and leaked) Resize/MutationObservers on every filter render,
    // which cancelled in-flight transitions and eventually froze the page.
    requestAnimationFrame(() => {
      const newDetails = newElement.querySelector('details');
      if (isOpen && newDetails) {
        newDetails.open = true;
      }
    });
  });
}


function viewMode() {
  class ViewModeController {
    constructor(gridId, desktopBtnSelector, mobileBtnSelector) {
      this.gridId = gridId;
      this.desktopBtnSelector = desktopBtnSelector;
      this.mobileBtnSelector = mobileBtnSelector;
      
      this.grid = document.getElementById(gridId);
      this.desktopButtons = document.querySelectorAll(desktopBtnSelector);
      this.mobileButtons = document.querySelectorAll(mobileBtnSelector);
      
      this.isCustomizer = window.Shopify?.designMode;
      this.tempStorage = { desktop: null, mobile: null };
      
      this.config = {
        desktop: { 
          key: 'productDesktopViewCols', 
          regex: /^lg:grid-cols-\d+$/, 
          prefix: 'lg:',
          default: '4'
        },
        mobile: { 
          key: 'productMobileViewCols', 
          regex: /^grid-cols-\d+(?!.*lg:)$/, 
          prefix: '',
          default: '2'
        }
      };
      
      this.currentMode = null;
      this.currentCols = null;
      
      this.init();
    }

    getDefaultCols(mode) {
      if (!this.grid) return this.config[mode].default;
      
      const dataAttr = this.grid.getAttribute(`data-${mode}-cols`);
      if (dataAttr) return dataAttr;
      
      const colClass = Array.from(this.grid.classList)
        .find(cls => this.config[mode].regex.test(cls));
      
      return colClass ? colClass.match(/\d+/)[0] : this.config[mode].default;
    }

    init() {
      if (!this.grid || (this.desktopButtons.length === 0 && this.mobileButtons.length === 0)) return;

      if (this.isCustomizer) {
        localStorage.removeItem(this.config.desktop.key);
        localStorage.removeItem(this.config.mobile.key);
        
        ['load', 'select'].forEach(event => {
          document.addEventListener(`shopify:section:${event}`, () => this.handleCustomizerUpdate());
        });
      }

      this.applyViewMode(true);
      this.registerEvents();
      
      let resizeTimeout;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          const newMode = this.getCurrentMode();
          if (newMode !== this.currentMode) this.applyViewMode();
        }, 150);
      });
    }

    handleCustomizerUpdate() {
      this.grid = document.getElementById(this.gridId);
      this.desktopButtons = document.querySelectorAll(this.desktopBtnSelector);
      this.mobileButtons = document.querySelectorAll(this.mobileBtnSelector);
      
      if (!this.grid) return;
      
      this.tempStorage = { desktop: null, mobile: null };
      this.currentMode = null;
      this.currentCols = null;
      this.applyViewMode(true);
      this.registerEvents();
    }

    getCurrentMode() {
      return window.matchMedia('(min-width: 64rem)').matches ? 'desktop' : 'mobile';
    }

    getSavedCols(mode) {
      const { key } = this.config[mode];
      
      if (this.isCustomizer) {
        return this.tempStorage[mode] || this.getDefaultCols(mode);
      }
      
      return localStorage.getItem(key) || this.getDefaultCols(mode);
    }

    applyViewMode(forceUpdate = false) {
      if (!this.grid) return;
      
      const mode = this.getCurrentMode();
      const cols = this.getSavedCols(mode);
      const buttons = mode === 'desktop' ? this.desktopButtons : this.mobileButtons;
      
      if (!buttons.length) return;
      
      if (forceUpdate || mode !== this.currentMode || cols !== this.currentCols) {
        const { regex, prefix } = this.config[mode];
        
        // Remove old grid classes
        Array.from(this.grid.classList)
          .filter(cls => regex.test(cls))
          .forEach(cls => this.grid.classList.remove(cls));
        
        // Add new grid class
        this.grid.classList.add(`${prefix}grid-cols-${cols}`);
        
        // Update active button
        buttons.forEach(btn => {
          btn.classList.toggle('active', btn.getAttribute('data-view-mode') === cols);
        });
        
        this.currentMode = mode;
        this.currentCols = cols;
        this.restartGridAnimations();
      }
    }

    registerEvents() {
      // Remove old listeners by replacing buttons
      const setupButtons = (buttons, mode) => {
        buttons.forEach(btn => {
          const newBtn = btn.cloneNode(true);
          btn.parentNode?.replaceChild(newBtn, btn);
          
          newBtn.addEventListener('click', () => {
            const value = newBtn.getAttribute('data-view-mode');
            
            if (this.isCustomizer) {
              this.tempStorage[mode] = value;
            } else {
              localStorage.setItem(this.config[mode].key, value);
            }
            
            this.applyViewMode(true);
          });
        });
      };
      
      if (this.desktopButtons.length) {
        setupButtons(this.desktopButtons, 'desktop');
        this.desktopButtons = document.querySelectorAll(this.desktopBtnSelector);
      }
      
      if (this.mobileButtons.length) {
        setupButtons(this.mobileButtons, 'mobile');
        this.mobileButtons = document.querySelectorAll(this.mobileBtnSelector);
      }
    }

    restartGridAnimations() {
      const elements = this.grid.querySelectorAll('.scroll-trigger, .animate--slide-in');
      
      elements.forEach(el => {
        el.classList.add('scroll-trigger--offscreen');
        el.classList.remove('scroll-trigger--cancel');
        if (el.hasAttribute('data-cascade')) {
          el.style.removeProperty('--animation-order');
        }
      });

      if (typeof initializeScrollAnimationTrigger === "function") {
        initializeScrollAnimationTrigger(this.grid);
      }
    }
  }

  new ViewModeController('product-grid', '.view-mode__button', '.view-mode__button-mobile');
}
